/**
 * stripSuspendedReferralCopy — keep the SUSPENDED Referral Program out of plan copy.
 *
 * WHY (measured on live staging, web-stg bundle main.64da3626.js, 2026-08-01)
 * --------------------------------------------------------------------------
 * PR #2479 hid 13 referral surfaces behind REFERRAL_PROGRAM_SUSPENDED. That flag is a
 * FRONTEND switch over FRONTEND components, so it cannot reach plan copy that arrives
 * as BACKEND data. Three live surfaces were still selling the paused programme:
 *
 *   1. Upgrade popup, Premium card  — "For Professionals! Supercharge your Wheels with
 *      monetization features and access to Referral Program revenue."
 *   2. Upgrade popup, Compare Plans — a feature row literally titled "Referral Program".
 *   3. Settings -> Subscription & Billing — the same Premium description again.
 *
 * Source: `GET https://app-stg.iyuga.net/subscription/packages/` -> Premium (id 6)
 * `short_description`. It is server data, so the only front-end-side close is at render.
 *
 * This is a RENDER-TIME GUARD, not the real fix. The durable fix is to correct the
 * package copy in the backend/admin. Until that lands, this stops the app advertising a
 * programme a user cannot join. It is deliberately inert when the programme resumes.
 *
 * Scope discipline: this only rewrites marketing copy. It never hides a price, a plan,
 * or a feature the user is actually buying.
 */

/** Matches a referral/earnings clause inside a sentence, including its lead-in conjunction. */
const REFERRAL_CLAUSE =
  /(?:,?\s*(?:and|plus|with)\s+)?(?:access to\s+)?(?:the\s+)?referral(?:\s+program)?(?:\s+(?:revenue|earnings|commissions?|income))?/gi;

/** A whole feature row / label that is nothing but the referral programme. */
const REFERRAL_LABEL = /^\s*(?:the\s+)?referral(?:\s+program)?\s*$/i;

/**
 * Is this string just a label for the referral programme (a comparison-table row)?
 * Callers use this to drop the row entirely rather than render an empty cell.
 */
export const isReferralLabel = (text) =>
  typeof text === "string" && REFERRAL_LABEL.test(text);

/**
 * Remove referral marketing from a copy string.
 *
 * @param {string} text        the copy as the backend sent it
 * @param {boolean} suspended  whether the programme is currently suspended
 * @returns {string} the copy with any referral clause removed and punctuation repaired
 */
export const stripSuspendedReferralCopy = (text, suspended) => {
  if (!suspended) return text;
  if (typeof text !== "string" || !text) return text;
  if (!/referral/i.test(text)) return text;

  return text
    .replace(REFERRAL_CLAUSE, "")
    // " features ." -> " features." and stray double spaces from the excision
    .replace(/\s+([.,!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    // a clause removed mid-list can leave a dangling separator before the full stop
    .replace(/[,;]\s*\./g, ".")
    .trim();
};

/**
 * Drop referral rows from the Compare-Plans feature map.
 *
 * `features` is `{ [heading]: string[] }` from `/subscription/comparison/`. Both
 * `PlanComparison` (the label column) and `SubscriptionFeatures` (each plan's
 * column) iterate this SAME object, so the row must be removed here — once, at the
 * source — or the two columns would fall out of vertical alignment by one row.
 *
 * A heading whose rows are all referral rows is dropped entirely rather than left
 * as an empty section header.
 *
 * @param {Object<string, string[]>} features
 * @param {boolean} suspended
 * @returns {Object<string, string[]>} the same object when not suspended
 */
export const stripReferralFeatureRows = (features, suspended) => {
  if (!suspended) return features;
  if (!features || typeof features !== "object") return features;

  return Object.entries(features).reduce((acc, [heading, rows]) => {
    if (isReferralLabel(heading)) return acc;
    const kept = Array.isArray(rows)
      ? rows.filter((row) => !isReferralLabel(row))
      : rows;
    if (Array.isArray(kept) && kept.length === 0) return acc;
    acc[heading] = kept;
    return acc;
  }, {});
};

export default stripSuspendedReferralCopy;
